import { useEffect } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import CheckMark from '../../../components/SVGIcons/CheckMark';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { useMarketingConsents } from '../../../hooks/useMarketingConsents';
import { analytics } from '../../../services/analytics';
import { auth } from '../../../services/auth';
import { useStores } from '../../../stores/useStores';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import styles from './SignUpPrivacy.module.scss';

export const SignUpPrivacy = observer((props: WizardChildProps) => {
  const { t } = useTranslation();
  const { portalStore, liveGeoStore, marketingConsentStore } = useStores();
  const { territoryLegalAge } = liveGeoStore.currentGeo ?? {};
  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
  const { handleSignUpWizard } = useHandleSignUpWizard();

  const {
    marketingConsents,
    onChangeMarketingConsent,
    setMarketingConsentsLS,
    removeMarketingConsentsLS,
    areAllMandatoryConsentsChecked,
  } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });

  useEffect(() => {
    if (!props?.isCurrent) {
      return;
    }
    void analytics.sendEvent('age_approve', {
      event_category: 'sign_up',
      event_action: 'impression',
    });
  }, [props?.isCurrent]);

  const handleSubmit = () => {
    void analytics.sendEvent('age_approve', {
      event_category: 'sign_up',
      event_action: 'click',
    });
    auth.isAgeConfirmed = true;
    setMarketingConsentsLS(marketingConsents);
    handleSignUpWizard({ provider: 'email', action: 'sign_up' });
  };

  const onClose = () => {
    void analytics.sendEvent('age_approve', {
      event_category: 'sign_up',
      event_action: 'click',
      click_location: 'close',
    });
    portalStore.closePortal();
    removeMarketingConsentsLS();
  };

  const legalAge = territoryLegalAge || 18;

  return (
    <>
      <DialogHeader closeBtnDataTestId="close-signup-dialog-btn" title={t('auth.almostDone')} onClose={onClose} />
      <DialogContent>
        <div className={styles.topContainer}>
          {/*
           * Two `<p>` slots, not one paragraph: these are block boxes on two lines, and collapsing
           * them would join the lines. The run list cannot see a boundary between sibling blocks.
           */}
          <Trans
            components={{
              intro: <p />,
              hint: <p className={styles.hint} />,
              links: <p className={styles.termsLink} data-testid="signup-privacy-terms-line" />,
              privacy: <a href="/pages/privacy-policy" rel="noreferrer" target="_blank" />,
              terms: <a href="/pages/terms-of-service" rel="noreferrer" target="_blank" />,
            }}
            i18nKey="auth.acceptTermsSentence"
          />
        </div>

        <p>{t('auth.confirmAgeAndTerritory', { legalAge })}</p>

        {signUpMarketingConsents.map((consent) => {
          const isChecked = marketingConsents[consent.id] ?? false;

          return (
            <div key={consent.id} className={styles.approveRow}>
              <Checkbox
                checked={isChecked}
                icon={<CheckMark />}
                id={consent.id}
                onChange={(checked) => onChangeMarketingConsent(checked, consent.id)}
              />
              <label className={styles.approveText} htmlFor={consent.id}>
                {consent.text}
              </label>
            </div>
          );
        })}

        <Button
          btnType="main"
          className={styles.acceptButton}
          dataTestId="signup-accept-terms-btn"
          disabled={!areAllMandatoryConsentsChecked}
          onClick={handleSubmit}
        >
          {t('common.buttons.accept')}
        </Button>
      </DialogContent>
    </>
  );
});
